'use client'

import React, { useEffect, useId, useRef } from 'react'

import { chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  checkboxInputStyles,
  checkboxLabelStyles,
  checkboxRootStyles,
} from './Checkbox.styles'

export type CheckboxProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> & {
  label?: React.ReactNode
  colorScheme?: ColorScheme
  /**
   * Overrides the label text token (e.g. a display-only checklist wanting
   * text/secondary instead of the disabled treatment). Omit for the default
   * label/disabled colors.
   */
  labelColor?: string
  /**
   * Set false to keep a disabled checkbox at full opacity — for display-only
   * rows (e.g. a completed checklist) that read as content, not controls.
   */
  dimWhenDisabled?: boolean
  /**
   * Overrides the unchecked box outline (the checked state keeps the accent).
   * Pairs with labelColor for display-only rows that read as content.
   */
  boxBorderColor?: string
  indeterminate?: boolean
}

export const Checkbox = ({
  label,
  colorScheme = 'dark',
  indeterminate,
  id,
  disabled = false,
  labelColor,
  dimWhenDisabled = true,
  boxBorderColor,
  readOnly = false,
  onClick,
  onChange,
  ...props
}: CheckboxProps) => {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate ?? false
    }
  }, [indeterminate])

  // readOnly is inert on checkbox inputs, so the toggle is cancelled here.
  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (readOnly) event.preventDefault()
    onClick?.(event)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return
    onChange?.(event)
  }

  return (
    <chakra.label htmlFor={inputId} {...checkboxRootStyles(Boolean(disabled))}>
      <chakra.input
        aria-readonly={readOnly || undefined}
        data-indeterminate={indeterminate ? '' : undefined}
        disabled={disabled}
        id={inputId}
        onChange={handleChange}
        onClick={handleClick}
        readOnly={readOnly}
        ref={inputRef}
        type='checkbox'
        {...checkboxInputStyles(
          colorScheme,
          Boolean(disabled),
          dimWhenDisabled,
          boxBorderColor
        )}
        {...props}
      />
      {label && (
        <chakra.span
          {...checkboxLabelStyles(colorScheme, Boolean(disabled), labelColor)}
        >
          {label}
        </chakra.span>
      )}
    </chakra.label>
  )
}
